import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box
} from '@chakra-ui/react';
import { MdOutlineGroupWork, MdCheck, MdClose } from 'react-icons/md';
import { GroupingCreationWizard, Modal, DetailedGroupingGrid, BulkSelectionBar, BulkEditButton } from "components";
import { useCreateGrouping, useDetailedStatusGroupings, useApproveGrouping, useRejectGrouping, useRemoveListingsFromGrouping, useBulkApproveGroupings, useBulkRejectGroupings } from "hooks/queries/useGroupings";
import { useArtists } from "hooks/queries/useArtists";
import { useAdminUsers } from "hooks/queries";
import { ItemModel, ListingModel } from "models";
import GroupingModel from "models/grouping/GroupingModel";
import { ProcessStatus } from "models/types";
import { useSetCreateAction } from "contexts/CreateActionContext";

export function GroupingOverview() {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(ProcessStatus.PENDING);
  const [page, setPage] = useState(1);
  const { data: groupingsPagination, isLoading } = useDetailedStatusGroupings(selectedStatus, page);
  const { data: artistsData } = useArtists();
  const { data: adminUsersData } = useAdminUsers();
  const createGroupingMutation = useCreateGrouping();
  const approveGroupingMutation = useApproveGrouping();
  const rejectGroupingMutation = useRejectGrouping();
  const removeListingsMutation = useRemoveListingsFromGrouping();
  const bulkApproveMutation = useBulkApproveGroupings();
  const bulkRejectMutation = useBulkRejectGroupings();
  const [isGroupingModalOpen, setIsGroupingModalOpen] = useState(false);
  const setCreateAction = useSetCreateAction();

  // Selection mode state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedGroupings, setSelectedGroupings] = useState<GroupingModel[]>([]);
  const visibleGroupingsRef = useRef<GroupingModel[]>([]);

  // Get groupings from pagination data
  const groupingList = groupingsPagination?.data || [];
  const hasPendingGroupings = groupingList.some(
    g => g.status.toLowerCase() === 'pending'
  );
  const artistsList = artistsData || [];
  const adminUsersList = adminUsersData || [];

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    setPage(1);
  };

  const handleGroupingModalOpen = useCallback(() => {
    if (createGroupingMutation.isPending) {
      return;
    }
    createGroupingMutation.reset();
    setIsGroupingModalOpen(true);
  }, [createGroupingMutation]);

  const handleGroupingModalClose = useCallback(() => {
    createGroupingMutation.reset();
    setIsGroupingModalOpen(false);
  }, [createGroupingMutation]);

  const handleGroupingSubmit = useCallback((item: ItemModel, listings: ListingModel[]) => {
    if (!item || listings.length === 0 || createGroupingMutation.isPending) {
      return;
    }

    const payload = {
      item_id: item.id,
      listings: listings.map((listing) => listing.id),
      auto_link: true
    };

    createGroupingMutation.mutate(payload, {
      onSuccess: () => {
        handleGroupingModalClose();
      }
    });
  }, [createGroupingMutation, handleGroupingModalClose]);

  const handleApprove = useCallback((groupingId: string) => {
    approveGroupingMutation.mutate({ grouping_id: groupingId });
  }, [approveGroupingMutation]);

  const handleReject = useCallback((groupingId: string) => {
    rejectGroupingMutation.mutate({ grouping_id: groupingId });
  }, [rejectGroupingMutation]);

  const handleRemoveListing = useCallback((groupingId: string, listingId: string) => {
    removeListingsMutation.mutate({
      id: groupingId,
      payload: { listings: [listingId] }
    });
  }, [removeListingsMutation]);

  // Selection mode handlers
  const handleEnterSelectionMode = useCallback(() => {
    if (!hasPendingGroupings) return;
    setSelectionMode(true);
    setSelectedGroupings([]);
  }, [hasPendingGroupings]);

  const handleExitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedGroupings([]);
  }, []);

  const handleGroupingSelect = useCallback((grouping: GroupingModel) => {
    setSelectedGroupings(prev => {
      const exists = prev.some(g => g.id === grouping.id);
      if (exists) {
        return prev.filter(g => g.id !== grouping.id);
      }
      return [...prev, grouping];
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    // Only select pending groupings from visible items
    const pendingGroupings = visibleGroupingsRef.current.filter(
      g => g.status.toLowerCase() === 'pending'
    );
    setSelectedGroupings(pendingGroupings);
  }, []);

  const handleFilteredGroupingsChange = useCallback((groupings: GroupingModel[]) => {
    visibleGroupingsRef.current = groupings;
  }, []);

  const handleBulkApprove = useCallback(async () => {
    if (selectedGroupings.length === 0) return;

    const groupingIds = selectedGroupings.map(g => g.id);
    await bulkApproveMutation.mutateAsync(groupingIds);
    handleExitSelectionMode();
  }, [selectedGroupings, bulkApproveMutation, handleExitSelectionMode]);

  const handleBulkReject = useCallback(async () => {
    if (selectedGroupings.length === 0) return;

    const groupingIds = selectedGroupings.map(g => g.id);
    await bulkRejectMutation.mutateAsync(groupingIds);
    handleExitSelectionMode();
  }, [selectedGroupings, bulkRejectMutation, handleExitSelectionMode]);

  useEffect(() => {
    if (selectionMode) {
      // In selection mode, hide the create action
      setCreateAction(null);
    } else {
      setCreateAction({
        icon: MdOutlineGroupWork,
        label: 'Create',
        onClick: handleGroupingModalOpen,
        isLoading: createGroupingMutation.isPending,
      });
    }

    return () => setCreateAction(null);
  }, [setCreateAction, handleGroupingModalOpen, createGroupingMutation.isPending, selectionMode]);

  // Filter to only count pending groupings for selection
  const pendingVisibleCount = visibleGroupingsRef.current.filter(
    g => g.status.toLowerCase() === 'pending'
  ).length;

  return (
    <>
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh" mx="auto">
        <DetailedGroupingGrid
          groupings={groupingList}
          isLoading={isLoading}
          onApprove={handleApprove}
          onReject={handleReject}
          onRemoveListing={handleRemoveListing}
          approveLoading={approveGroupingMutation.isPending}
          rejectLoading={rejectGroupingMutation.isPending}
          pagination={groupingsPagination}
          onPageChange={setPage}
          defaultStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          defaultAccordionOpen={true}
          availableArtists={artistsList}
          availableAdminUsers={adminUsersList}
          selectionMode={selectionMode}
          selectedGroupings={selectedGroupings}
          onGroupingSelect={handleGroupingSelect}
          onFilteredGroupingsChange={handleFilteredGroupingsChange}
        />
      </Box>

      <BulkEditButton
        onClick={handleEnterSelectionMode}
        isVisible={!selectionMode && hasPendingGroupings}
      />

      {/* Selection Bar - shown when in selection mode */}
      {selectionMode && (
        <BulkSelectionBar
          selectedCount={selectedGroupings.length}
          totalCount={pendingVisibleCount}
          onSelectAll={handleSelectAll}
          actions={[
            {
              label: 'Approve',
              colorScheme: 'green',
              icon: MdCheck,
              onClick: handleBulkApprove
            },
            {
              label: 'Reject',
              colorScheme: 'red',
              icon: MdClose,
              onClick: handleBulkReject
            }
          ]}
          onCancel={handleExitSelectionMode}
          isLoading={bulkApproveMutation.isPending || bulkRejectMutation.isPending}
          entityName="grouping"
        />
      )}

      <Modal
        isOpen={isGroupingModalOpen}
        onClose={handleGroupingModalClose}
        title="Create Grouping"
        content={
          <GroupingCreationWizard
            onSubmit={handleGroupingSubmit}
            isLoading={createGroupingMutation.isPending}
          />
        }
        showCloseButton={false}
        showSubmitButton={false}
        size="4xl"
      />
    </>
  );
}

export default GroupingOverview;
